import { Injectable } from '@angular/core';
import type { Tenant } from './tenant.service';

export type ImportErrorType = 'INVALID_JSON' | 'NOT_AN_ARRAY' | 'FILE_READ_ERROR';

export interface ImportResult {
  tenants: Tenant[];
  error?: ImportErrorType;
}

@Injectable({
  providedIn: 'root'
})
export class TenantImportExportService {
  public exportToFile(tenants: Tenant[]): void {
    const json = JSON.stringify(tenants, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'cmi-tenants.json';
    link.click();
    URL.revokeObjectURL(url);
  }

  public async importFromFile(file: File): Promise<ImportResult> {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>): void => {
        try {
          const content = e.target?.result as string;
          const data = JSON.parse(content) as unknown[];

          if (!Array.isArray(data)) {
            resolve({ tenants: [], error: 'NOT_AN_ARRAY' });
            return;
          }

          const validTenants = this._validateAndFilterTenants(data);
          resolve({ tenants: validTenants });
        } catch {
          resolve({ tenants: [], error: 'INVALID_JSON' });
        }
      };

      reader.onerror = (): void => {
        resolve({ tenants: [], error: 'FILE_READ_ERROR' });
      };

      reader.readAsText(file);
    });
  }

  private _validateAndFilterTenants(data: unknown[]): Tenant[] {
    return data
      .filter((item): item is { name: string; apiUrl?: string; stsUrl?: string } => {
        if (typeof item !== 'object' || item === null) {
          return false;
        }
        const obj = item as Record<string, unknown>;
        return 'name' in obj && typeof obj['name'] === 'string';
      })
      .map((item) => ({
        name: item.name,
        apiUrl: item.apiUrl || '',
        stsUrl: item.stsUrl || ''
      }));
  }
}
